import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserManagementPanel from './UserManagement/UserManagementPanel';
import UserEditModal from './UserManagement/UserEditModal';
import ActionsPanel from './Actions/ActionsPanel';
import FeedbackPanel from './Feedback/FeedbackPanel';
import apiService from '../../services/api';
import './AdminPage.scss';

const AdminPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Centralized State
  const [activeTab, setActiveTab] = useState('user-management');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userGameStats, setUserGameStats] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // State for UserTable, now managed by the parent
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    limit: 20 // Using a consistent page size
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const tabs = [
    { id: 'user-management', label: 'User Management' },
    { id: 'actions', label: 'Actions' },
    { id: 'feedback', label: 'Feedback' }
  ];

  // The main data fetching function, now uses state for search term and page
  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        search: searchTerm,
        page: page.toString(),
        limit: pagination.limit.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      const response = await apiService.get(`/api/admin/users?${params}`);

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setPagination(prev => ({ ...prev, ...data.pagination, currentPage: page }));
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, pagination.limit, sortBy, sortOrder]);

  const fetchDashboardStats = async () => {
    try {
      const response = await apiService.get('/api/admin/dashboard');

      if (response.ok) {
        const stats = await response.json();
        setDashboardStats(stats);
      } else {
        console.error('Failed to fetch dashboard stats');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  // Initial dashboard and user fetch
  useEffect(() => {
    if (isAuthenticated && user && ['admin', 'owner'].includes(user.role)) {
      fetchDashboardStats();
      fetchUsers(1); // Initial fetch for the user table
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);
  
  // Debounced search effect
  useEffect(() => {
    const handler = setTimeout(() => {
      // Avoid fetching on initial render if user isn't loaded yet
      if (user) {
        fetchUsers(1); // Reset to page 1 on new search
      }
    }, 500);
    return () => clearTimeout(handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, user]);

  // Refetch users when sorting changes
  useEffect(() => {
    if (user) {
      fetchUsers(pagination.currentPage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  const fetchUserGameStats = async (userId) => {
    try {
      const response = await apiService.get(`/api/admin/users/${userId}/stats`);

      if (response.ok) {
        const stats = await response.json();
        setUserGameStats(stats);
      } else {
        console.error('Failed to fetch user game stats');
        setUserGameStats([]);
      }
    } catch (error) {
      console.error('Error fetching user game stats:', error);
      setUserGameStats([]);
    }
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    if (user) {
      await fetchUserGameStats(user.id);
    } else {
      setUserGameStats([]);
    }
  };
  
  const handleUserEdit = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUserUpdate = async (userId, userData) => {
    try {
      const response = await apiService.put(`/api/admin/users/${userId}`, userData);

      if (response.ok) {
        setShowEditModal(false);
        await fetchUsers(pagination.currentPage); // Refresh table on success
        if (selectedUser && selectedUser.id === userId) {
          const updatedData = await response.json();
          setSelectedUser(updatedData.user);
        }
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.error };
      }
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: 'Failed to update user' };
    }
  };
  
  const handleGameStatsUpdate = async (userId, gameStats) => {
    try {
      const response = await apiService.put(`/api/admin/users/${userId}/stats`, { gameStats });

      if (response.ok) {
        await fetchUserGameStats(userId);
        await fetchUsers(pagination.currentPage);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.error };
      }
    } catch (error) {
      console.error('Error updating game stats:', error);
      return { success: false, error: 'Failed to update game statistics' };
    }
  };

  const handleSort = (column) => {
    const newSortOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(column);
    setSortOrder(newSortOrder);
    // Reset to first page when sorting changes
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  if (authLoading) {
    return <div></div>;
  }

  if (!isAuthenticated || !user || !['admin', 'owner'].includes(user.role)) {
    navigate('/');
    return null;
  }

  return (
    <div className="admin-page">
      <div className="admin-page__container">
        <div className="admin-page__header">
          <h1>Admin Dashboard</h1>
          <div className="admin-page__user-info">
            Logged in as: <strong>{user.username}</strong> ({user.role})
          </div>
        </div>

        {dashboardStats && (
          <div className="admin-page__dashboard-stats">
            <div className="admin-page__stat-card">
              <h3>Total Users</h3>
              <div className="admin-page__stat-value">{dashboardStats.totalUsers}</div>
            </div>
            <div className="admin-page__stat-card">
              <h3>New Today</h3>
              <div className="admin-page__stat-value">{dashboardStats.newUsersToday}</div>
            </div>
            <div className="admin-page__stat-card">
              <h3>New This Week</h3>
              <div className="admin-page__stat-value">{dashboardStats.newUsersThisWeek}</div>
            </div>
            <div className="admin-page__stat-card">
              <h3>Daily Games</h3>
              <div className="admin-page__stat-value">{dashboardStats.dailyGamesToday}</div>
            </div>
            <div className="admin-page__stat-card">
              <h3>Total Games</h3>
              <div className="admin-page__stat-value">{dashboardStats.totalGamesToday}</div>
            </div>
          </div>
        )}

        {error && (
          <div className="admin-page__error">{error}</div>
        )}

        <div className="admin-page__tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`admin-page__tab ${activeTab === tab.id ? 'admin-page__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="admin-page__content">
          {activeTab === 'user-management' && (
            <UserManagementPanel
              users={users}
              selectedUser={selectedUser}
              userGameStats={userGameStats}
              loading={loading}
              pagination={pagination}
              onUserSelect={handleUserSelect}
              onUserEdit={handleUserEdit}
              searchTerm={searchTerm}
              onSearch={setSearchTerm}
              onPageChange={fetchUsers}
              onStatsUpdate={handleGameStatsUpdate}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          )}

          {activeTab === 'actions' && (
            <ActionsPanel />
          )}

          {activeTab === 'feedback' && (
            <FeedbackPanel />
          )}
        </div>

        {showEditModal && selectedUser && (
          <UserEditModal
            user={selectedUser}
            onClose={() => setShowEditModal(false)}
            onUpdate={handleUserUpdate}
            currentUserRole={user.role}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPage;